import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { PercentInput } from '@/components/form/baseInputs/PercentInput';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { Settings04Icon } from '@/components/icons/Settings04Icon';
import { XCircleIcon } from '@/components/icons/XCircleIcon';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';
import { TaxDragConfigCalculationMethod } from '@/types/schema';

import {
  GrowthProfileModalFormPaths,
  GrowthProfileModalFormShape,
} from '../GrowthProfileModal.types';
import { ComputedTaxDragPanel } from './ComputedTaxDragPanel';
import { TaxDragRateFormShape } from './TaxDrag.types';
import { getDisplayedTaxDragRate } from './TaxDrag.utils';

interface TaxDragRateFieldProps {
  label: string;
  rate: TaxDragRateFormShape;
  ratePath: `taxDrag.globalRate` | `taxDrag.overrides.${number}`;
  stateCode?: string | null;
  contextualHelp?: JSX.Element;
}

export function TaxDragRateField({
  label,
  rate,
  ratePath,
  stateCode,
  contextualHelp,
}: TaxDragRateFieldProps) {
  const { disabled } = useFormFieldsDisabled();
  const { control, setValue } = useFormContext<GrowthProfileModalFormShape>();
  const isComputed =
    rate.calculationMethod === TaxDragConfigCalculationMethod.Computed;
  const calculationMethodPath =
    `${ratePath}.calculationMethod` as GrowthProfileModalFormPaths;

  const setCalculationMethod = (method: TaxDragConfigCalculationMethod) => {
    setValue(calculationMethodPath, method, { shouldDirty: true });
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" gap={1.5} alignItems="flex-start">
        <Box sx={{ width: 150 }}>
          {isComputed ? (
            <PercentInput
              id={`${ratePath}.computedTaxDragPercentage`}
              name={`${ratePath}.computedTaxDragPercentage`}
              label={label}
              contextualHelp={contextualHelp}
              value={getDisplayedTaxDragRate(rate)?.toFixed(2) ?? ''}
              disabled
              fixedDecimalScale
            />
          ) : (
            <FormAwarePercentInput<GrowthProfileModalFormShape>
              control={control}
              fieldName={
                `${ratePath}.taxDragPercentage` as GrowthProfileModalFormPaths
              }
              label={label}
              contextualHelp={contextualHelp}
              isDecimalJSInput
              required
            />
          )}
        </Box>
        <IconButton
          ariaLabel={`Compute ${label.toLowerCase()}`}
          disabled={disabled}
          variant="secondary"
          size="sm"
          icon={Settings04Icon}
          sx={{ mt: 2.5 }}
          onClick={() =>
            setCalculationMethod(TaxDragConfigCalculationMethod.Computed)
          }
        />
        <Button
          aria-label="Clear computed tax drag"
          disabled={disabled || !isComputed}
          variant="transparent"
          size="xs"
          startIcon={XCircleIcon}
          sx={{ ml: 'auto', mt: 3 }}
          onClick={() =>
            setCalculationMethod(TaxDragConfigCalculationMethod.Manual)
          }
        >
          Clear computed tax drag
        </Button>
      </Stack>
      {isComputed && (
        <Typography
          variant="body-xs"
          sx={{ mt: '4px !important', color: COLORS.TEXT.PRIMARY }}
        >
          Computed effective tax drag
        </Typography>
      )}
      {isComputed && (
        <ComputedTaxDragPanel
          rate={rate}
          ratePath={ratePath}
          stateCode={stateCode}
        />
      )}
    </Stack>
  );
}
